<template>
  <div class="ArticleJobsForm">
    <Field
      v-for="(jobOption, index) in jobListArticle"
      :key="index"
      :label="jobOption.label"
      type="checkbox"
      :value="jobs.includes(jobOption)"
      @update:value="toggleJob(jobOption, $event)" />
    <div class="sections">
      <div
        v-for="sectionTemplate in sectionsSelected"
        :key="sectionTemplate.id"
        class="section">
        <RowFlex>
          <Tag>
            {{ sectionTemplate.name }}
          </Tag>
          <Btn
            class="xs ib danger light"
            @click="unselectSectionTemplateId(sectionTemplate.id)">
            <Icon name="cross" />
          </Btn>
        </RowFlex>
        <Field
          v-for="(jobOption, index) in jobListSections"
          :key="index"
          :label="jobOption.label"
          type="checkbox"
          :value="hasJob({
            sectionTemplateId: sectionTemplate.id,
            key: jobOption.key,
          })"
          @update:value="toggleSectionJob(sectionTemplate.id, jobOption, $event)" />
      </div>
      <RowFlex>
        <Field
          :label="$t('section')"
          :options="sectionTemplatesOptions"
          type="select"
          :value.sync="sectionTemplateIdToAdd" />
        <Btn class="ib light" @click="addSection">
          {{ $t('add') }}
        </Btn>
      </RowFlex>
    </div>
  </div>
</template>

<script>
import { adjustBy, removeItem, toggleItem } from '@utils/collections'
import {
  any, append, assoc, evolve, has, mergeLeft, modifyPath,
  pipe, pluck, prop, reject, unless, whereEq,
} from 'rambda'
import { getters } from '@utils'

export default {
  props: {
    jobs: { type: Array, required: true },
  },
  data() {
    return {
      jobListArticle: [
        {
          assign: mergeLeft({ doPickProducts: true }),
          key: 'doPickProducts',
          label: this.$t('pickProducts'),
        },
        {
          assign: mergeLeft({ doPickCoverImage: true }),
          key: 'doPickCoverImage',
          label: this.$t('pickCoverImage'),
        },
        {
          assign: mergeLeft({ doGenerateTitle: true }),
          key: 'doGenerateTitle',
          label: this.$t('generateTitle'),
        },
        {
          assign: mergeLeft({ doGenerateDescription: true }),
          key: 'doGenerateDescription',
          label: this.$t('generateDescription'),
        },
        {
          assign: mergeLeft({ doCreateChildren: true }),
          key: 'doCreateChildren',
          label: this.$t('doCreateChildren'),
        },
        {
          assign: mergeLeft({ doPost: true }),
          key: 'doPost',
          label: this.$t('post'),
        },
      ],
      jobListSections: [
        {
          assign: mergeLeft({ doSelectImage: true }),
          key: 'doSelectImage',
          label: this.$t('selectImage'),
        },
        {
          assign: mergeLeft({ doCreateVariant: true }),
          key: 'doCreateVariant',
          label: this.$t('doCreateVariant'),
        },
        {
          assign(section) {
            const { factsTemplates } = this
              .queryOne('sectionTemplate', section.sectionTemplateId)

            return {
              ...section,
              factsToGenerate: pluck('type', factsTemplates),
            }
          },
          key: 'factsToGenerate',
          label: this.$t('factsToGenerate'),
        },
      ],

      sectionTemplateIdsSelected: [],
      sectionTemplateIdToAdd: null,
    }
  },
  computed: {
    sectionsSelected() {
      return this.sectionTemplateIdsSelected
        .map(sectionTemplateId => this
          .queryOne('sectionTemplate', sectionTemplateId))
    },
    ...getters('sectionTemplatesOptions'),
  },
  methods: {
    hasJob(query) {
      return any(whereEq(query), this.jobs)
    },
    toggleSectionJob(sectionTemplateId, jobOption, doSelect) {
      const jobs = doSelect
        ? [
          ...this.jobs,
          pipe(
            evolve({
              assign: inSection(sectionTemplateId, this),
            }),
            mergeLeft({ sectionTemplateId }),
          )(jobOption),
        ]
        : reject(
          whereEq({
            sectionTemplateId,
            key: jobOption.key,
          }),
          this.jobs,
        )

      this.$up('jobs', jobs)
    },
    toggleJob(jobOption, doSelect) {
      const jobs = toggleItem(jobOption, this.jobs)

      this.$up('jobs', jobs)
    },

    addSection() {
      this.sectionTemplateIdsSelected = [
        ...this.sectionTemplateIdsSelected,
        this.sectionTemplateIdToAdd,
      ]

      this.sectionTemplateIdToAdd = null
    },
    unselectSectionTemplateId(sectionTemplateId) {
      this.$up('jobs', reject(whereEq({ sectionTemplateId }), this.jobs))

      this.sectionTemplateIdsSelected = removeItem(
        sectionTemplateId,
        this.sectionTemplateIdsSelected,
      )
    },
  },
}

const inSection = (sectionTemplateId, self) => assign => pipe(
  // ensure sections as a whole exist
  unless(
    has('sections'),
    assoc('sections', []),
  ),

  // ensure section exists
  unless(
    pipe(
      prop('sections'),
      any(whereEq({ sectionTemplateId })),
    ),
    modifyPath(['sections'], append({ sectionTemplateId })),
  ),

  evolve({
    sections: adjustBy(
      whereEq({ sectionTemplateId }),
      assign.bind(self),
    ),
  }),
)
</script>

<style lang="scss">
.ArticleJobsForm {
  .sections {
    margin-bottom: $u;
  }

  .section {
    border-bottom: 1px solid $border-color;
    padding: $u;
  }
}
</style>

<i18n>
{
  "en": {
    "add": "Add",
    "doCreateChildren": "Create child posts",
    "doCreateVariant": "Generate text",
    "factsToGenerate": "Generate facts",
    "generateDescription": "Generate description",
    "generateTitle": "Generate title",
    "pickCoverImage": "Pick cover image",
    "pickProducts": "Pick products",
    "post": "Post (or update an existing post)",
    "selectImage": "Select section image",
    "section": "Section"
  }
}
</i18n>
